import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ColumnCustomizerModal from "../ColumnCustomizerModal";
import DataRefreshControl from "../DataRefreshControl";
import ListPaginationBar from "../ListPaginationBar";
import { getTableFreezeColsClassName, SortableTableTh, TableActionColumnHeader, TableViewport } from "../TableShell";
import TabLabelWithCount from "../TabLabelWithCount";
import { STOCK_TAB_QUICK_ANALYSIS } from "../StockSectionTabs";
import ListFiltersSlideOver from "../entity/ListFiltersSlideOver";
import ListFiltersToolbarButton from "../entity/ListFiltersToolbarButton";
import CrudListToolbar from "../entity/CrudListToolbar";
import EntityLabelCell from "../entity/EntityLabelCell";
import EntityLabelFilterBar from "../entity/EntityLabelFilterBar";
import EntityLabelsModal from "../entity/EntityLabelsModal";
import EntityListBulkToolbar from "../entity/EntityListBulkToolbar";
import ImportCsvPanel from "../entity/ImportCsvPanel";
import { useAdvancedFilter } from "../../shared/hooks/useAdvancedFilter";
import { useEntityLabelFilter } from "../../shared/hooks/useEntityLabelFilter";
import { useEntityListChrome } from "../../shared/hooks/useEntityListChrome";
import { useLatestRequestSeq } from "../../shared/hooks/useLatestRequestSeq";
import { useListPagination } from "../../shared/hooks/useListPagination";
import {
  deleteStockQuickAnalysisFile,
  getStockQuickAnalysisFile,
  listStockQuickAnalysisFiles,
  removeStockQuickAnalysisLabel,
  renameStockQuickAnalysisLabel,
  saveStockQuickAnalysisFile,
  updateStockQuickAnalysisRowLabels,
} from "../../shared/api/stockQuickAnalysisFiles";
import {
  advancedFilterAppliedToFormValues,
  applyAdvancedFilterRows,
  patchAdvancedFilterCriteria,
} from "../../shared/utils/advancedFilter";
import { getMessageForFetchError } from "../../shared/utils/apiError";
import { attachValidatedCsvFileImport, downloadCsvTextFromString } from "../../shared/utils/csvFileHelpers";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../../shared/utils/entityListSelection";
import { getSharedLabelsAmongItems } from "../../shared/utils/entityBulkLabels";
import {
  ENTITY_LABEL_FILTER_UNLABELED_DISPLAY,
  entityLabelsEqual,
  getItemLabels,
  isUnlabeledEntityLabelFilter,
  normalizeEntityLabelsArray,
} from "../../shared/utils/entityLabelFilter";
import {
  applyQuickAnalysisRowLabels,
  buildQuickAnalysisCsvExportText,
  buildQuickAnalysisFilterFields,
  buildQuickAnalysisFilterMatchers,
  parseQuickAnalysisCsv,
  quickAnalysisPercentClassName,
  quickAnalysisRowMatchesSearch,
} from "../../shared/utils/quickAnalysisCsv";
import { RowActionIcons } from "../../shared/icons/IconLib";
import { sortRowsByConfig } from "../../shared/utils/sortRowsByConfig";

const QA_FILE_PARAM = "qaFile";

/**
 * Quick Analysis CSV files saved as text. Each uploaded file name is a sub-tab.
 */
export default function StockQuickAnalysis({
  stockSession,
  serverUrl,
  onError,
  onRowCountChange,
  setConfirmDialog,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const fileIdFromUrl = (searchParams.get(QA_FILE_PARAM) || "").trim();
  const [files, setFiles] = useState([]);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [dataset, setDataset] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState([]);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [importPanelExpanded, setImportPanelExpanded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [labelUpdating, setLabelUpdating] = useState(false);
  const [bulkLabelsOpen, setBulkLabelsOpen] = useState(false);
  const fileInputRef = useRef(null);
  const datasetRef = useRef(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  datasetRef.current = dataset;
  const nextRequest = useLatestRequestSeq();

  const reportError = useCallback((message) => {
    if (typeof onErrorRef.current === "function") onErrorRef.current(message || "");
  }, []);

  const {
    selectedIds,
    setSelectedIds,
    searchTerm,
    setSearchTerm,
    sortConfig,
    setSortConfig,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({
    initialSort: { key: null, direction: "asc" },
  });

  const filterFields = useMemo(
    () => buildQuickAnalysisFilterFields(dataset?.columns || []),
    [dataset?.columns]
  );

  const {
    open: filterOpen,
    setOpen: setFilterOpen,
    applied: filterApplied,
    setApplied: setFilterApplied,
    activeCount: filterActiveCount,
    activeSummary: filterActiveSummary,
    clearApplied: clearFilter,
  } = useAdvancedFilter({ fields: filterFields });

  const selectFile = useCallback(
    (id) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", STOCK_TAB_QUICK_ANALYSIS);
      if (id) params.set(QA_FILE_PARAM, id);
      else params.delete(QA_FILE_PARAM);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const applyParsedFile = useCallback(
    (file, parsed) => {
      setDataset({
        id: file.id,
        fileName: file.fileName,
        uploadedAt: file.uploadedAt,
        columns: parsed.columns,
        rows: parsed.rows,
        keyTypes: parsed.keyTypes,
      });
      setVisibleColumnIds(parsed.columns.map((column) => column.id));
      setSearchTerm("");
      setSortConfig({ key: null, direction: "asc" });
      setSelectedIds(new Set());
      clearFilter();
    },
    [clearFilter, setSearchTerm, setSelectedIds, setSortConfig]
  );

  useEffect(() => {
    let cancelled = false;
    if (!serverUrl || !stockSession?.account_id) {
      setFiles([]);
      setFilesLoaded(true);
      return undefined;
    }
    (async () => {
      try {
        const { files: next } = await listStockQuickAnalysisFiles(serverUrl, stockSession);
        if (!cancelled) setFiles(Array.isArray(next) ? next : []);
      } catch (err) {
        if (!cancelled) {
          reportError(getMessageForFetchError(err, "Failed to load saved CSV files"));
        }
      } finally {
        if (!cancelled) setFilesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serverUrl, stockSession, reportError]);

  useEffect(() => {
    if (!filesLoaded || !serverUrl || !stockSession?.account_id) return undefined;
    if (!files.length) {
      setDataset(null);
      if (fileIdFromUrl) selectFile("");
      return undefined;
    }
    const requested = files.find((file) => file.id === fileIdFromUrl) || files[0];
    if (requested.id !== fileIdFromUrl) {
      selectFile(requested.id);
      return undefined;
    }
    const current = datasetRef.current;
    if (current?.id === requested.id && current?.uploadedAt === requested.uploadedAt) {
      return undefined;
    }

    let cancelled = false;
    const { isCurrent } = nextRequest();
    setLoadingFile(true);
    if (current?.id !== requested.id) setDataset(null);
    (async () => {
      try {
        const file = await getStockQuickAnalysisFile(serverUrl, stockSession, requested.id);
        if (cancelled || !isCurrent()) return;
        const parsed = parseQuickAnalysisCsv(file.csvText);
        applyParsedFile(file, {
          ...parsed,
          rows: applyQuickAnalysisRowLabels(parsed.rows, file.rowLabels),
        });
        reportError("");
      } catch (err) {
        if (cancelled || !isCurrent()) return;
        setDataset(null);
        reportError(getMessageForFetchError(err, err?.message || "Could not read that CSV."));
      } finally {
        if (!cancelled && isCurrent()) setLoadingFile(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    filesLoaded,
    files,
    fileIdFromUrl,
    serverUrl,
    stockSession,
    selectFile,
    nextRequest,
    applyParsedFile,
    reportError,
  ]);

  const columns = dataset?.columns || [];
  const rows = dataset?.rows || [];
  const hasSymbolColumn = columns.some(
    (column) => column.id === "symbol" || String(column.label || "").trim().toLowerCase() === "symbol"
  );
  const {
    activeLabel,
    setActiveLabel,
    labelOptions,
    filteredItems: labelFilteredRows,
    addLabelName,
    renameLabelName,
    removeLabelName,
  } = useEntityLabelFilter({ items: rows, scopeKey: dataset?.id || "" });

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeLabel, setSelectedIds]);
  const activeMeta = files.find((file) => file.id === (dataset?.id || fileIdFromUrl)) || null;
  const displayedCount = dataset ? rows.length : activeMeta?.rowCount ?? 0;

  useEffect(() => {
    if (typeof onRowCountChange === "function") onRowCountChange(displayedCount);
    return () => {
      if (typeof onRowCountChange === "function") onRowCountChange(0);
    };
  }, [displayedCount, onRowCountChange]);

  const filterValues = useMemo(
    () => advancedFilterAppliedToFormValues(filterApplied, filterFields),
    [filterApplied, filterFields]
  );

  const handleFilterFieldChange = useCallback(
    (fieldId, value) => {
      setFilterApplied((prev) => patchAdvancedFilterCriteria(prev, fieldId, value));
    },
    [setFilterApplied]
  );

  const filterMatchers = useMemo(() => buildQuickAnalysisFilterMatchers(columns), [columns]);

  const filteredRows = useMemo(() => {
    const searched = labelFilteredRows.filter((row) => quickAnalysisRowMatchesSearch(row, searchTerm));
    return applyAdvancedFilterRows(searched, filterApplied, { matchers: filterMatchers });
  }, [labelFilteredRows, searchTerm, filterApplied, filterMatchers]);

  const sortedRows = useMemo(
    () => sortRowsByConfig(filteredRows, sortConfig, dataset?.keyTypes || {}),
    [filteredRows, sortConfig, dataset]
  );

  const { pageItems, paginationBarProps, getSelectAllCheckboxProps } = useListPagination(sortedRows, [
    rows.length,
    searchTerm,
    sortConfig.key,
    sortConfig.direction,
    filterActiveCount,
    filterApplied,
    activeLabel,
  ]);

  const visibleColumns = useMemo(() => {
    const byId = new Map(columns.map((column) => [column.id, column]));
    return visibleColumnIds.map((id) => byId.get(id)).filter(Boolean);
  }, [columns, visibleColumnIds]);

  const columnPickerColumns = useMemo(
    () => columns.map((column) => ({ id: column.id, label: column.label })),
    [columns]
  );

  function upsertFileSummary(summary) {
    setFiles((prev) => {
      const rest = prev.filter((file) => file.id !== summary.id);
      return [summary, ...rest].sort((a, b) => {
        const aTime = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
        const bTime = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
        return bTime - aTime;
      });
    });
  }

  async function importFromCsv(file) {
    if (!serverUrl || !stockSession?.account_id) {
      reportError("Sign in before saving a CSV file.");
      return;
    }
    const { isCurrent } = nextRequest();
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = parseQuickAnalysisCsv(text);
      const saved = await saveStockQuickAnalysisFile(serverUrl, stockSession, {
        fileName: file.name || "upload.csv",
        csvText: text,
        rowCount: parsed.rows.length,
        created_by: stockSession.user_id || "",
      });
      if (!isCurrent()) return;
      applyParsedFile(saved, {
        ...parsed,
        rows: applyQuickAnalysisRowLabels(parsed.rows, saved.rowLabels),
      });
      upsertFileSummary(saved);
      if (saved?.id && saved.id !== fileIdFromUrl) selectFile(saved.id);
      reportError("");
      setImportPanelExpanded(false);
    } catch (err) {
      if (!isCurrent()) return;
      reportError(getMessageForFetchError(err, err?.message || "Could not read that CSV."));
    } finally {
      if (isCurrent()) {
        setImporting(false);
        setLoadingFile(false);
      }
    }
  }

  function handleFileChange(event) {
    attachValidatedCsvFileImport(event, importFromCsv, (message) => {
      reportError(message);
    });
  }

  function openFileImport() {
    const input = fileInputRef.current;
    if (!input || importing) return;
    input.click();
  }

  async function deleteActiveFile() {
    if (!dataset?.id || !serverUrl) return;
    const removedId = dataset.id;
    const { isCurrent } = nextRequest();
    setDeletingFile(true);
    try {
      await deleteStockQuickAnalysisFile(serverUrl, stockSession, removedId);
      if (!isCurrent()) return;
      setDataset(null);
      setFiles((prev) => prev.filter((file) => file.id !== removedId));
      reportError("");
    } catch (err) {
      if (!isCurrent()) return;
      reportError(getMessageForFetchError(err, "Failed to delete the CSV file"));
    } finally {
      if (isCurrent()) setDeletingFile(false);
    }
  }

  function confirmDeleteFile() {
    if (!dataset || typeof setConfirmDialog !== "function") return;
    const fileName = dataset.fileName || "this file";
    setConfirmDialog({
      open: true,
      title: "Delete file",
      mode: "confirm",
      message: `Delete "${fileName}"? The saved CSV will be removed.`,
      onConfirm: () => {
        deleteActiveFile();
      },
    });
  }

  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);
  const selectedRows = sortedRows.filter((row) => row && selectedVisibleIds.has(row._id));
  const sharedBulkLabels = getSharedLabelsAmongItems(selectedRows);

  function replaceRowLabels(rowLabels) {
    setDataset((prev) => {
      if (!prev) return prev;
      return { ...prev, rows: applyQuickAnalysisRowLabels(prev.rows, rowLabels) };
    });
  }

  function showLabelAlert(title, message) {
    if (typeof setConfirmDialog === "function") {
      setConfirmDialog({
        open: true,
        title,
        message,
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    reportError(message);
  }

  async function persistRowLabelUpdates(updates) {
    if (!dataset?.id) {
      throw new Error("Upload a CSV before assigning labels.");
    }
    const rowLabels = await updateStockQuickAnalysisRowLabels(serverUrl, stockSession, {
      fileId: dataset.id,
      updates,
    });
    replaceRowLabels(rowLabels);
  }

  function handleAddLabel(name) {
    const result = addLabelName(name);
    if (!result.ok) showLabelAlert("Add label", result.error);
    return result;
  }

  async function handleRenameLabel(oldLabel, newLabel) {
    if (!dataset?.id) throw new Error("Upload a CSV before renaming labels.");
    setLabelUpdating(true);
    try {
      const rowLabels = await renameStockQuickAnalysisLabel(serverUrl, stockSession, {
        fileId: dataset.id,
        oldLabel,
        newLabel,
      });
      const result = renameLabelName(oldLabel, newLabel);
      if (!result.ok) throw new Error(result.error || "Failed to rename label");
      replaceRowLabels(rowLabels);
    } catch (err) {
      reportError(getMessageForFetchError(err, "Failed to rename label"));
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  async function handleRemoveLabel(label) {
    if (!dataset?.id) throw new Error("Upload a CSV before removing labels.");
    setLabelUpdating(true);
    try {
      const rowLabels = await removeStockQuickAnalysisLabel(serverUrl, stockSession, {
        fileId: dataset.id,
        label,
      });
      const result = removeLabelName(label);
      if (!result.ok) throw new Error(result.error || "Failed to remove label");
      replaceRowLabels(rowLabels);
    } catch (err) {
      reportError(getMessageForFetchError(err, "Failed to remove label"));
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  async function handleApplyLabelsForRow(row, labels) {
    if (!row?.rowKey) {
      const message = "This row has no Symbol, so it cannot be labeled.";
      showLabelAlert("Labels", message);
      throw new Error(message);
    }
    const nextLabels = normalizeEntityLabelsArray(labels);
    if (entityLabelsEqual(getItemLabels(row), nextLabels)) return;
    setLabelUpdating(true);
    try {
      await persistRowLabelUpdates([{ rowKey: row.rowKey, labels: nextLabels }]);
    } catch (err) {
      reportError(getMessageForFetchError(err, "Failed to update labels"));
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  function openBulkLabels() {
    if (selectedVisibleCount === 0) {
      showLabelAlert("Update labels", "Please select at least one row to update");
      return;
    }
    setBulkLabelsOpen(true);
  }

  async function handleBulkApplyLabels(labels) {
    if (selectedRows.length === 0) {
      setBulkLabelsOpen(false);
      return;
    }
    if (selectedRows.some((row) => !row.rowKey)) {
      const message = "Every selected row needs a Symbol before it can be labeled.";
      showLabelAlert("Update labels", message);
      throw new Error(message);
    }
    const nextLabels = normalizeEntityLabelsArray(labels);
    const updates = [];
    const seen = new Set();
    for (const row of selectedRows) {
      if (seen.has(row.rowKey)) continue;
      seen.add(row.rowKey);
      if (entityLabelsEqual(getItemLabels(row), nextLabels)) continue;
      updates.push({ rowKey: row.rowKey, labels: nextLabels });
    }
    setLabelUpdating(true);
    try {
      if (updates.length > 0) await persistRowLabelUpdates(updates);
      setSelectedIds(new Set());
      setBulkLabelsOpen(false);
    } catch (err) {
      reportError(getMessageForFetchError(err, "Failed to update labels"));
      throw err;
    } finally {
      setLabelUpdating(false);
    }
  }

  const emptyLabelMessage =
    labelFilteredRows.length === 0 && activeLabel != null
      ? isUnlabeledEntityLabelFilter(activeLabel)
        ? `No ${ENTITY_LABEL_FILTER_UNLABELED_DISPLAY.toLowerCase()} rows. Assign labels from the label icon in Action or choose All.`
        : `No rows with label “${activeLabel}”. Assign this label from the label icon in Action or choose All.`
      : "No rows match the current search or filters.";

  function showExportAlert(message) {
    if (typeof setConfirmDialog === "function") {
      setConfirmDialog({
        open: true,
        title: "Export",
        message,
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    reportError(message);
  }

  function exportSelectedCsv() {
    if (selectedVisibleIds.size === 0) {
      showExportAlert("Select at least one row to export.");
      return;
    }
    setExporting(true);
    try {
      const selected = sortedRows.filter((row) => row && selectedVisibleIds.has(row._id));
      if (selected.length === 0) {
        showExportAlert("No matching rows found for the current selection.");
        return;
      }
      const csv = buildQuickAnalysisCsvExportText(selected, visibleColumns);
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsvTextFromString(`quick-analysis-export-${datePart}.csv`, csv);
      reportError("");
    } catch (err) {
      reportError(err?.message || "Error exporting CSV.");
    } finally {
      setExporting(false);
    }
  }

  const emptyColSpan = Math.max(1, visibleColumns.length + 2);
  const activeFileId = dataset?.id || fileIdFromUrl;
  const uploadedAt = dataset?.uploadedAt || activeMeta?.uploadedAt || null;

  return (
    <TableViewport fitSecondFreezeColumn>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="input-file-offscreen"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleFileChange}
      />
      <ColumnCustomizerModal
        open={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        allColumns={columnPickerColumns}
        visibleIds={visibleColumnIds}
        onChangeVisibleIds={setVisibleColumnIds}
        saving={false}
        onReset={() => {
          setVisibleColumnIds(columns.map((column) => column.id));
          setColumnModalOpen(false);
        }}
        onSave={(ids) => {
          setVisibleColumnIds(ids);
          setColumnModalOpen(false);
        }}
      />
      <EntityLabelsModal
        open={bulkLabelsOpen}
        onClose={() => {
          if (labelUpdating) return;
          setBulkLabelsOpen(false);
        }}
        labelOptions={labelOptions}
        selectedLabels={sharedBulkLabels}
        onApplyLabels={handleBulkApplyLabels}
        onAddLabel={handleAddLabel}
        onRenameLabel={handleRenameLabel}
        onRemoveLabel={handleRemoveLabel}
        setConfirmDialog={setConfirmDialog}
        saving={labelUpdating}
        subtitle={`Select labels to apply to ${selectedVisibleCount} selected row${selectedVisibleCount === 1 ? "" : "s"}. Rename or delete from the same row.`}
      />
      <ListFiltersSlideOver
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        subtitle="A row must match every field you fill in."
        fields={filterFields}
        values={filterValues}
        onFieldChange={handleFilterFieldChange}
        onClear={clearFilter}
      />

      {files.length > 0 ? (
        <div className="tabs-bar">
          <div className="tabs-list" role="tablist" aria-label="Saved CSV files">
            {files.map((file) => (
              <button
                key={file.id}
                type="button"
                role="tab"
                aria-selected={file.id === activeFileId}
                className={`tab-btn tab-btn--file-name ${file.id === activeFileId ? "active" : ""}`}
                title={file.fileName}
                onClick={() => selectFile(file.id)}
              >
                <TabLabelWithCount label={file.fileName} count={file.rowCount} />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <ImportCsvPanel
        expanded={importPanelExpanded}
        fileInputId="quick-analysis-csv"
        importing={importing}
        columnHint="First row is the header. Columns are detected from that row. The file is saved under its name."
        onFileChange={handleFileChange}
      />

      <CrudListToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search all columns"
        onClearSearch={() => setSearchTerm("")}
        middleSlot={
          <ListFiltersToolbarButton
            onClick={() => setFilterOpen(true)}
            activeCount={filterActiveCount}
            activeSummary={filterActiveSummary}
            disabled={columns.length === 0}
            title={columns.length === 0 ? "Upload a CSV before filtering" : undefined}
          />
        }
        importPanelExpanded={importPanelExpanded}
        onToggleImport={() => setImportPanelExpanded((open) => !open)}
        showCsvImport
        showNew={false}
        showDelete={false}
        showViewToggle={false}
        extraActions={
          <>
            {dataset ? (
              <button
                type="button"
                className="btn btn-md btn-secondary"
                disabled={deletingFile || importing}
                onClick={confirmDeleteFile}
              >
                {deletingFile ? "Deleting…" : "Delete file"}
              </button>
            ) : null}
            <DataRefreshControl
              source="a CSV file"
              lastRefreshedAt={uploadedAt}
              refreshing={importing}
              disabled={importing}
              onRefresh={openFileImport}
              title={
                importing
                  ? "Import in progress"
                  : "Choose a CSV file to import. There is no external link to refresh from."
              }
            />
            <button
              type="button"
              className="btn btn-md btn-primary-outline"
              disabled={!dataset || selectedVisibleCount === 0 || exporting}
              onClick={() => exportSelectedCsv()}
            >
              {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
            </button>
          </>
        }
      />

      {dataset ? (
        <EntityLabelFilterBar
          items={rows}
          activeLabel={activeLabel}
          onActiveLabelChange={setActiveLabel}
          labelOptions={labelOptions}
          ariaLabel="Quick Analysis labels"
          showUnlabeledFilter
        />
      ) : null}

      {!filesLoaded || loadingFile ? (
        <p className="panel-hint">Loading saved CSV…</p>
      ) : dataset ? (
        <p className="panel-hint">
          {dataset.fileName} · {rows.length} {rows.length === 1 ? "row" : "rows"} · {columns.length}{" "}
          {columns.length === 1 ? "column" : "columns"}
          {hasSymbolColumn ? "" : " A Symbol column is required to assign labels."}
        </p>
      ) : (
        <p className="panel-hint">Upload a CSV to analyze it. The first row is the header.</p>
      )}

      <EntityListBulkToolbar
        selectedCount={selectedVisibleCount}
        onUpdateLabels={openBulkLabels}
        updating={labelUpdating && bulkLabelsOpen}
        ariaLabel="Bulk quick analysis actions"
        updateLabel="Labels"
      />

      {dataset ? (
        <>
          <ListPaginationBar {...paginationBarProps} />
          <div className="table-scroll">
            <div
              className={`table-wrapper table-wrapper-stock-mgmt table-wrapper--sticky-header ${getTableFreezeColsClassName(2, 1)}`.trim()}
            >
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        {...getSelectAllCheckboxProps(handleSelectAll, selectedIds)}
                        aria-label="Select all rows on this page"
                      />
                    </th>
                    {visibleColumns.map((column) => (
                      <SortableTableTh
                        key={column.id}
                        sortKey={column.id}
                        currentSortKey={sortConfig.key}
                        direction={sortConfig.direction}
                        onSort={handleSort}
                        className={column.type === "number" ? "table-cell-numeric" : ""}
                      >
                        {column.label}
                      </SortableTableTh>
                    ))}
                    <TableActionColumnHeader
                      onCustomizeColumns={() => setColumnModalOpen(true)}
                      customizeDisabled={columns.length === 0}
                    />
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={emptyColSpan} className="empty-message">
                        {emptyLabelMessage}
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((row) => (
                      <tr key={row._id}>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row._id)}
                            onChange={(e) => handleSelectOne(row._id, e.target.checked)}
                            aria-label={`Select row ${row._id}`}
                          />
                        </td>
                        {visibleColumns.map((column) => {
                          const text = row.cells?.[column.id] ?? "";
                          const className = [
                            column.type === "number" ? "table-cell-numeric" : "",
                            quickAnalysisPercentClassName(text),
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <td key={column.id} className={className || undefined}>
                              {text}
                            </td>
                          );
                        })}
                        <td className="text-center table-col-action">
                          <RowActionIcons>
                            <EntityLabelCell
                              item={row}
                              iconOnly
                              labelOptions={labelOptions}
                              disabled={!row.rowKey}
                              saving={labelUpdating}
                              ariaLabel="Row labels"
                              onApplyLabels={(labels) => handleApplyLabelsForRow(row, labels)}
                              onAddLabel={handleAddLabel}
                              onRenameLabel={handleRenameLabel}
                              onRemoveLabel={handleRemoveLabel}
                              setConfirmDialog={setConfirmDialog}
                            />
                          </RowActionIcons>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </TableViewport>
  );
}
